import http, { handleHttpError, handleHttpResponse } from 'services/http'
import { HTTPResponse, IPaginationResponse } from 'services/http/http.types'
import { IProjectCheckpoint, IProjectCheckpointArtifact, IProjectCheckpointCriterion } from './projectCheckpoints.types'

const getList = async (): Promise<HTTPResponse<IPaginationResponse<IProjectCheckpoint>>> => {
  const response = await http
    .get<IProjectCheckpoint[]>('/nsi/v1/project-checkpoints')
    .then(handleHttpResponse)
    .catch(handleHttpError)
  if (response.status === 'success') {
    return {
      status: response.status,
      body: {
        nextPage: null,
        prevPage: null,
        totalCount: response.body.length,
        items: response.body,
      },
    }
  }
  return response
}

const getAll = (): Promise<HTTPResponse<IProjectCheckpoint[]>> => {
  return http.get<IProjectCheckpoint[]>('/nsi/v1/project-checkpoints').then(handleHttpResponse).catch(handleHttpError)
}

const getOne = async (id: IProjectCheckpoint['id']): Promise<HTTPResponse<IProjectCheckpoint>> => {
  return http
    .get('/nsi/v1/project-checkpoints/' + id)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

export interface ICreateProjectCheckpointDto {
  name: string
  description?: string
  duration: number
}

const create = async (dto: ICreateProjectCheckpointDto): Promise<HTTPResponse<IProjectCheckpoint>> => {
  return http.post('/nsi/v1/project-checkpoints', dto).then(handleHttpResponse).catch(handleHttpError)
}

const update = async (
  dto: Partial<ICreateProjectCheckpointDto & { isVisible: boolean }>,
  id: IProjectCheckpoint['id']
): Promise<HTTPResponse<IProjectCheckpoint>> => {
  return http
    .patch('/nsi/v1/project-checkpoints/' + id, dto)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getCriteria = async (
  checkpointId: IProjectCheckpoint['id']
): Promise<HTTPResponse<IProjectCheckpointCriterion[]>> => {
  return http
    .get(`/nsi/v1/project-checkpoints/${checkpointId}/criterias`)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const createCriterion = async (
  checkpointId: IProjectCheckpoint['id'],
  dto: Omit<IProjectCheckpointCriterion, 'id'>
): Promise<HTTPResponse<IProjectCheckpointCriterion>> => {
  return http
    .post(`/nsi/v1/project-checkpoints/${checkpointId}/criterias`, dto)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const updateCriterion = async (
  checkpointId: IProjectCheckpoint['id'],
  id: IProjectCheckpointCriterion['id'],
  dto: Partial<Omit<IProjectCheckpointCriterion, 'id'>>
): Promise<HTTPResponse<IProjectCheckpointCriterion>> => {
  return http
    .patch(`/nsi/v1/project-checkpoints/${checkpointId}/criterias/${id}`, dto)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const deleteCriterion = async (
  checkpointId: IProjectCheckpoint['id'],
  id: IProjectCheckpointCriterion['id']
): Promise<HTTPResponse<IProjectCheckpointCriterion>> => {
  return http
    .delete(`/nsi/v1/project-checkpoints/${checkpointId}/criterias/${id}`)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getArtifacts = async (
  checkpointId: IProjectCheckpoint['id']
): Promise<HTTPResponse<IProjectCheckpointArtifact[]>> => {
  return http
    .get(`/nsi/v1/project-checkpoints/${checkpointId}/expected-artefacts`)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const createArtifact = async (
  checkpointId: IProjectCheckpoint['id'],
  dto: Omit<IProjectCheckpointArtifact, 'id'>
): Promise<HTTPResponse<IProjectCheckpointArtifact>> => {
  return http
    .post(`/nsi/v1/project-checkpoints/${checkpointId}/expected-artefacts`, dto)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const updateArtifact = async (
  checkpointId: IProjectCheckpoint['id'],
  id: IProjectCheckpointArtifact['id'],
  dto: Partial<Omit<IProjectCheckpointArtifact, 'id'>>
): Promise<HTTPResponse<IProjectCheckpointArtifact>> => {
  return http
    .patch(`/nsi/v1/project-checkpoints/${checkpointId}/expected-artefacts/${id}`, dto)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const deleteArtifact = async (
  checkpointId: IProjectCheckpoint['id'],
  id: IProjectCheckpointArtifact['id']
): Promise<HTTPResponse<IProjectCheckpointArtifact>> => {
  return http
    .delete(`/nsi/v1/project-checkpoints/${checkpointId}/expected-artefacts/${id}`)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const projectCheckpointsApi = {
  update,
  create,
  getOne,
  getList,
  getAll,
  getCriteria,
  createCriterion,
  updateCriterion,
  deleteCriterion,
  getArtifacts,
  createArtifact,
  updateArtifact,
  deleteArtifact,
}

export default projectCheckpointsApi
